import { Navigate, Route, Routes } from 'react-router-dom'
import LibraryPage from './pages/LibraryPage'

import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import OnboardingRoute from './components/OnboardingRoute'
import ProtectedRoute from './components/ProtectedRoute'
import PublicOnlyRoute from './components/PublicOnlyRoute'
import ProUpgradeModal from './components/shared/ProUpgradeModal'

import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import OAuthCallbackPage from './pages/OAuthCallbackPage'
import HomePage from './pages/HomePage'
import OnboardingPage from './pages/OnboardingPage'
import NotFoundPage from './pages/NotFoundPage'
import DeckDetailPage from './pages/DeckDetailPage'
import CourseDetailPage from './pages/CourseDetailPage'
import ClassicStudyPage from './pages/ClassicStudyPage'
import LearnPage from './pages/LearnPage'
import CatalogCourseDetailPage from './pages/CatalogCourseDetailPage'
import GrammarListPage from './pages/GrammarListPage'
import GrammarArticlePage from './pages/GrammarArticlePage'
import ProUpgradePage from './pages/ProUpgradePage'
import SharedDeckPage from './pages/SharedDeckPage'
import SharedCoursePage from './pages/SharedCoursePage'
import SharedCardPage from './pages/SharedCardPage'
import SharedTextPage from './pages/SharedTextPage'
import TextImportPage from './pages/TextImportPage'
import WordReviewPage from './pages/WordReviewPage'
import TextImportStatusPage from './pages/TextImportStatusPage'
import TextFlowSourcePage from './pages/TextFlowSourcePage'
import TextFlowWordsPage from './pages/TextFlowWordsPage'
import TextFlowSavePage from './pages/TextFlowSavePage'
import TextPage from './pages/TextPage'
import TextEditPage from './pages/TextEditPage'
import TextEditWordsPage from './pages/TextEditWordsPage'
import TextPlayerPage from './pages/TextPlayerPage'
import BillingSuccessPage from './pages/BillingSuccessPage'
import SettingsPage from './pages/SettingsPage'
import SmartQueuePage from './pages/SmartQueuePage'
import AdminPage from './pages/AdminPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminActivityPage from './pages/AdminActivityPage'
import AdminRoute from './components/AdminRoute'
import ProgressPage from './pages/ProgressPage'
import QueueComposePage from './pages/QueueComposePage'
import ShareTargetPage from './pages/ShareTargetPage'
import DesignDemoPage from './pages/DesignDemoPage'
import { libraryHref } from './utils/libraryAddress'
// ExplorePage is no longer a standalone route — Explore is a segment within LibraryPage → CatalogPage.

export default function App() {
  return (
    <ErrorBoundary>
    <Routes>
      {/* ── Root: landing for unauthenticated, redirect for authenticated ── */}
      {/* ── Public only (redirect to /home or /onboarding if authenticated) ── */}
      <Route element={<PublicOnlyRoute />}>
        <Route index element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      {/* ── Onboarding (auth required, redirects away if already onboarded) ─ */}
      <Route element={<OnboardingRoute />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
      </Route>

      {/* ── Design-system primitive demo (no auth, no layout) ─────────── */}
      <Route path="/design-demo" element={<DesignDemoPage />} />

      {/* ── OAuth callback (no auth required, no layout — handles its own state) ── */}
      <Route path="/oauth/:provider/callback" element={<OAuthCallbackPage />} />

      {/* ── Password reset apply (ADR-061 §4 — public, session-less, unlisted) ── */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* ── Billing redirects (no layout, no auth required for success page) ── */}
      <Route path="/billing/success" element={<BillingSuccessPage />} />

      {/* ── Share Target (PWA share sheet entry point — handles auth internally) ── */}
      <Route path="/share-target" element={<ShareTargetPage />} />

      {/* ── Shared (no auth required, no layout) ─────────────────────── */}
      <Route path="/shared/deck/:token/study" element={<ClassicStudyPage />} />
      <Route path="/shared/deck/:token" element={<SharedDeckPage />} />
      <Route path="/shared/course/:token" element={<SharedCoursePage />} />
      <Route path="/shared/card/:token" element={<SharedCardPage />} />
      {/* A text by its share link (handoff §17.2): read and practise with no sign-in. */}
      <Route path="/t/:token" element={<SharedTextPage />} />

      {/* ── Protected (authenticated + onboarded + sidebar layout) ───── */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/home" element={<HomePage />} />

          {/* Permanent redirect — bookmarks/old links land here */}
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />

          {/* Decks — /decks list redirects to Library so there is one canonical hub */}
          <Route path="/decks" element={<Navigate to={libraryHref('mine')} replace />} />
          <Route path="/decks/:id" element={<DeckDetailPage />} />

          {/* Courses — /courses lands on Library → Catalog → Courses (ADR-106 Q10);
              courses are Catalog content, Mine has no courses tab */}
          <Route path="/courses" element={<Navigate to={libraryHref('catalog', 'courses')} replace />} />
          <Route path="/courses/:id" element={<CourseDetailPage />} />

          {/* Text Import */}
          <Route path="/text-import" element={<TextImportPage />} />
          <Route path="/text-import/:id/review" element={<WordReviewPage />} />
          <Route path="/text-import/:id/status" element={<TextImportStatusPage />} />

          {/* Text creation flow (ADR-115): source, words, save; then a stand-in landing
              that task 6's text screen replaces. The old /text-import screens above
              stay until a later decision retires or repoints them. */}
          <Route path="/texts/new" element={<TextFlowSourcePage />} />
          <Route path="/texts/new/words" element={<TextFlowWordsPage />} />
          <Route path="/texts/new/save" element={<TextFlowSavePage />} />
          {/* The text screen: a list row, a deck's pane, and the creation flow's landing. */}
          <Route path="/texts/:textId" element={<TextPage />} />
          {/* Edit mode (handoff §15) and its word picker: full-screen like the flow. */}
          <Route path="/texts/:textId/edit" element={<TextEditPage />} />
          <Route path="/texts/:textId/edit/words" element={<TextEditWordsPage />} />
          {/* The player (handoff §10–§14): full-screen, its own strip and keyboard. */}
          <Route path="/texts/:textId/practice" element={<TextPlayerPage />} />

          {/* Library hub — unified Mine / Catalog surface */}
          <Route path="/library" element={<LibraryPage />} />
          {/* Deep-link redirect: /catalog lands on the hub catalog segment */}
          <Route path="/catalog" element={<Navigate to={libraryHref('catalog', 'decks')} replace />} />
          {/* /explore deep-links into Library → Catalog → Explore segment */}
          <Route path="/explore" element={<Navigate to={libraryHref('catalog', 'explore')} replace />} />

          {/* Progress */}
          <Route path="/progress" element={<ProgressPage />} />

          {/* Queue composition — sources feeding the smart queue */}
          <Route path="/queue" element={<QueueComposePage />} />
          <Route path="/catalog/:id" element={<CatalogCourseDetailPage />} />
          <Route path="/grammar" element={<GrammarListPage />} />
          <Route path="/grammar/:slug" element={<GrammarArticlePage />} />
          <Route path="/pro" element={<ProUpgradePage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* Study modes — full-screen, no sidebar layout */}
        <Route path="/decks/:deckId/study" element={<ClassicStudyPage />} />
        <Route path="/decks/:deckId/learn" element={<LearnPage />} />
        <Route path="/folders/:folderId/learn" element={<LearnPage />} />
        <Route path="/courses/:courseId/learn" element={<LearnPage />} />
        {/* Smart queue — assembles cards from all decks, then hands off to /session */}
        <Route path="/learn" element={<SmartQueuePage />} />
        {/* Session — LearnPage driven by a pre-built session (from smart queue) */}
        <Route path="/session" element={<LearnPage />} />
      </Route>

      {/* ── Admin panel (is_admin required, no sidebar) ───────────────── */}
      <Route element={<AdminRoute />}>
        <Route element={<Layout />}>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/activity" element={<AdminActivityPage />} />
        </Route>
      </Route>

      {/* ── 404 ──────────────────────────────────────────────────────── */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    {/* Global Pro upgrade modal — driven by useProModalStore. Mounted at the
        app root so it works inside fullscreen routes (Learn, Study) too, not
        only inside Layout-wrapped pages. */}
    <ProUpgradeModal />
    </ErrorBoundary>
  )
}
